import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { User } from '../types';
import { useAuth } from '../components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const UserManagement: React.FC = () => {
  const { user: currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  useOrderNotifications();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'user' as 'admin' | 'user' });
  const usersPerPage = 12;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    console.log('Fetching users...');
    
    const { data, error } = await supabase
      .from('habib_users')
      .select('id, username, role, is_active, created_at, updated_at, last_login')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    
    console.log('Users query result:', { data, error });
    
    if (error) {
      console.error('Error fetching users:', error);
    }
    
    if (data) {
      console.log('Setting users:', data);
      setUsers(data);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        await supabase.rpc('update_user', {
          p_id: editingUser.id,
          p_username: formData.username,
          p_password: formData.password || null,
          p_role: formData.role
        });
      } else {
        await supabase.rpc('create_user', {
          p_username: formData.username,
          p_password: formData.password,
          p_role: formData.role
        });
      }
      
      setShowModal(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user. Please try again.');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ username: user.username, password: '', role: user.role });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('areYouSureDelete'))) {
      await supabase.from('habib_users').delete().eq('id', id);
      fetchUsers();
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('userManagement')}</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher />
              <button onClick={() => navigate('/admin')} className="bg-white hover:bg-green-50 border px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('back')}
              </button>
              <span className="hidden md:block text-sm font-medium" style={{color: '#05472A'}}>{t('admin')}: {currentUser?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">{t('userManagement')}</h2>
          <p className="text-slate-600 text-sm sm:text-base">{t('manageSystemUsers')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchUsers}
            className="px-4 py-3 bg-white border rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium"
            style={{color: '#05472A', borderColor: '#05472A40'}}
          >
            {t('refresh')}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-3 text-white rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium"
            style={{backgroundColor: '#05472A'}}
          >
            {t('addNewUser')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-4 sm:p-6" style={{borderColor: '#05472A20'}}>
        <div className="relative max-w-md">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('searchUsers')}
            className="w-full px-4 py-3 pl-12 pr-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
            style={{borderColor: '#05472A20'}}
          />
          <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {loading ? (
        <div className="card-elevated p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('loadingUsers')}</p>
        </div>
      ) : currentUsers.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-soft p-8 text-center border" style={{borderColor: '#05472A20'}}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{color: '#05472A'}}>{t('noUsersFound')}</h3>
          <p className="text-slate-600">{t('noUsersMatch')}</p>
          <p className="text-xs text-slate-400 mt-2">{t('totalUsers2')}: {users.length} | {t('filtered')}: {filteredUsers.length}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {currentUsers.map((user) => (
            <div key={user.id} className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-6" style={{borderColor: '#05472A20'}}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#05472A'}}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{user.username}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user.role === 'admin' 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role}
                      </span>
                      <span className="text-sm text-slate-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(user)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                  >
                    {t('edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                  >
                    {t('deleteUser')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{borderColor: '#05472A40', color: '#05472A'}}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-4 py-2 rounded-xl transition-colors ${
                currentPage === page ? 'text-white' : 'border hover:bg-green-50'
              }`}
              style={currentPage === page ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{borderColor: '#05472A40', color: '#05472A'}}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-bold mb-4" style={{color: '#05472A'}}>
              {editingUser ? t('editUser') : t('addNewUser')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('username')}</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20'}}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('password')} {editingUser && t('leaveEmptyPassword')}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20'}}
                  required={!editingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('role')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value as 'admin' | 'user'})}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20'}}
                >
                  <option value="user">{t('user')}</option>
                  <option value="admin">{t('admin')}</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    setFormData({ username: '', password: '', role: 'user' });
                  }}
                  className="flex-1 px-4 py-3 border rounded-xl font-semibold transition-colors hover:bg-gray-50"
                  style={{borderColor: '#05472A40', color: '#05472A'}}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 text-white rounded-xl font-semibold transition-colors"
                  style={{backgroundColor: '#05472A'}}
                >
                  {editingUser ? t('update') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </main>
    </div>
  );
};

export default UserManagement;
